"use client";

import { Button, Toast, cn } from "@heroui/react";
import { useState, type ReactNode } from "react";

export interface Snippet {
  /** Tab label — "cURL", "JavaScript", "Python", "Go", "JSON", … */
  label: string;
  language:
    | "bash"
    | "javascript"
    | "python"
    | "go"
    | "rust"
    | "php"
    | "ruby"
    | "json";
  code: string;
}

interface CodeTabsProps {
  snippets: Snippet[];
  className?: string;
}

/*
 * Regex tokenizer instead of a highlighter dependency: the docs only show
 * cURL, fetch/requests calls and JSON, so a handful of rules per language
 * covers everything on the page. Rules are tried in order; earlier wins.
 * Inner regexes must not contain capture groups.
 */
type Rule = [token: string, pattern: RegExp];

const GRAMMARS: Record<Snippet["language"], Rule[]> = {
  bash: [
    ["comment", /#[^\n]*/],
    ["string", /"(?:[^"\\]|\\.)*"|'[^']*'/],
    ["variable", /\$\{[^}]*\}|\$\([^)]*\)|\$\w+/],
    ["keyword", /^\s*curl\b|\b(?:GET|POST|DELETE|PUT|PATCH)\b/],
    ["property", /(?<=\s)--?[A-Za-z][\w-]*/],
    ["number", /\b\d+\b/],
  ],
  javascript: [
    ["comment", /\/\/[^\n]*/],
    ["string", /`(?:[^`\\]|\\.)*`|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/],
    [
      "keyword",
      /\b(?:const|let|var|async|await|function|return|new|import|export|from|while|for|of|if|else)\b/,
    ],
    ["variable", /\bprocess\.env\.\w+/],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  python: [
    ["comment", /#[^\n]*/],
    ["string", /f?"(?:[^"\\]|\\.)*"|f?'(?:[^'\\]|\\.)*'/],
    [
      "keyword",
      /\b(?:import|from|with|as|open|while|for|in|def|return|if|else|not|and|or|print)\b/,
    ],
    ["variable", /\bos\.environ\b/],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  go: [
    ["comment", /\/\/[^\n]*/],
    ["string", /"(?:[^"\\]|\\.)*"|`[^`]*`/],
    [
      "keyword",
      /\b(?:package|import|func|var|const|defer|go|if|else|for|range|return|nil)\b/,
    ],
    ["variable", /\bos\.Getenv\b/],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  rust: [
    ["comment", /\/\/[^\n]*/],
    ["string", /"(?:[^"\\]|\\.)*"/],
    [
      "keyword",
      /\b(?:use|fn|let|mut|match|async|await|return|Some|None|Ok|Err)\b/,
    ],
    ["variable", /\bstd::env::var\b/],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  php: [
    ["comment", /\/\/[^\n]*|#[^\n]*/],
    ["string", /"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/],
    ["variable", /\$\w+/],
    [
      "keyword",
      /<\?php|\b(?:new|true|false|null|echo|require|use|function|return)\b/,
    ],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  ruby: [
    ["comment", /#[^\n]*/],
    ["string", /"(?:[^"\\]|\\.)*"/],
    ["keyword", /\b(?:require|def|end|do|true|false|nil|puts)\b/],
    ["variable", /\bENV\b/],
    ["number", /\b\d+(?:\.\d+)?\b/],
  ],
  json: [
    ["property", /"(?:[^"\\]|\\.)*"(?=\s*:)/],
    ["string", /"(?:[^"\\]|\\.)*"/],
    ["keyword", /\b(?:true|false|null)\b/],
    ["number", /-?\b\d+(?:\.\d+)?\b/],
  ],
};

function highlight(code: string, language: Snippet["language"]): ReactNode[] {
  const rules = GRAMMARS[language];
  const combined = new RegExp(
    rules.map(([, pattern]) => `(${pattern.source})`).join("|"),
    "gm",
  );

  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of code.matchAll(combined)) {
    const index = match.index ?? 0;
    if (index > last) nodes.push(code.slice(last, index));
    const group = match.slice(1).findIndex((g) => g !== undefined);
    nodes.push(
      <span key={index} style={{ color: `var(--code-${rules[group][0]})` }}>
        {match[0]}
      </span>,
    );
    last = index + match[0].length;
  }
  if (last < code.length) nodes.push(code.slice(last));
  return nodes;
}

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="size-3.5" aria-hidden>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V6a2 2 0 0 1 2-2h9" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="size-3.5" aria-hidden>
    <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Syntax-highlighted code block with a language switcher, for the API docs.
 * One snippet renders as a plain labelled block; several render as tabs.
 * CodeBlock stays the choice for copy-first UI like key reveal.
 */
export function CodeTabs({ snippets, className }: CodeTabsProps) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const current = snippets[active];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(current.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      Toast.toast.danger("Couldn't copy. Copy it manually instead.");
    }
  }

  return (
    <div
      className={cn(
        // min-w-0: stops a long code line from widening the parent track.
        "bg-surface-secondary border-default/60 w-full min-w-0 overflow-hidden rounded-lg border",
        className,
      )}
    >
      <div className="border-default/60 flex items-center justify-between gap-2 border-b px-2 py-1.5">
        {/* Scrollable: seven languages overflow the header on small screens,
            and in any narrow column. `no-scrollbar` hides the native bar,
            which on a 28px-tall tab row is thicker than the tabs themselves
            and reads as a broken widget rather than an affordance. The row
            still scrolls by wheel, trackpad and touch, and every tab is
            reachable by keyboard, so nothing is hidden behind the gesture. */}
        <div
          className="no-scrollbar flex min-w-0 items-center gap-1 overflow-x-auto"
          role="tablist"
          aria-label="Language"
        >
          {snippets.map((snippet, index) => (
            <button
              key={snippet.label}
              type="button"
              role="tab"
              aria-selected={index === active}
              onClick={() => {
                setActive(index);
                setCopied(false);
              }}
              className={cn(
                "tap tap-sm rounded-md px-2 py-1 font-mono text-xs",
                index === active
                  ? "bg-surface text-foreground shadow-sm"
                  : "text-muted hover:text-foreground",
              )}
            >
              {snippet.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onPress={handleCopy}
          aria-label={copied ? "Copied to clipboard" : "Copy code"}
          className="h-7 gap-1.5 px-2 text-xs"
        >
          {/* Keyed on `copied` so React remounts the icon and the animation
              replays on every copy, rather than only the first. */}
          <span key={copied ? "done" : "idle"} className={copied ? "tap-confirm" : undefined}>
            {copied ? <CheckIcon /> : <CopyIcon />}
          </span>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>

      <pre className="overflow-x-auto px-3 py-3 text-xs leading-relaxed">
        <code className="font-mono">{highlight(current.code, current.language)}</code>
      </pre>
    </div>
  );
}
